import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';
import { signInWithMicrosoft, signOut, syncCurrentUserAccess } from '@/services/firebase';

const LoginPage = () => {
  const navigate = useNavigate();
  const { user, setUser, loading } = useAuthStore();

  useEffect(() => {
    if (user && !loading) {
      navigate('/admin', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleMicrosoftSignIn = async () => {
    try {
      const user = await signInWithMicrosoft();
      await syncCurrentUserAccess();
      await user.getIdToken(true);
      await setUser(user);
      navigate('/admin', { replace: true });
    } catch (error: any) {
      console.error('Sign in error:', error);
      await signOut();
      
      // Provide detailed error messages
      let errorMessage = 'Failed to sign in. ';
      
      if (error?.code) {
        switch (error.code) {
          case 'auth/popup-blocked':
            errorMessage += 'Popup was blocked by your browser. Please allow popups for this site.';
            break;
          case 'auth/popup-closed-by-user':
            errorMessage += 'Sign-in popup was closed. Please try again.';
            break;
          case 'auth/unauthorized-domain':
            errorMessage += 'This domain is not authorized. Please contact your administrator.';
            break;
          case 'auth/invalid-credential':
            errorMessage += 'Invalid credentials. Please check your Azure AD configuration.';
            break;
          case 'auth/configuration-not-found':
            errorMessage += 'OIDC provider not configured. Please check Firebase Authentication settings.';
            break;
          case 'auth/operation-not-allowed':
            errorMessage += 'OIDC provider is not enabled in Firebase. Go to Firebase Console → Authentication → Sign-in method → Enable "OpenID Connect" provider with Provider ID: oidc.microsoft';
            break;
          case 'auth/network-request-failed':
            errorMessage += 'Network error. Please check your internet connection.';
            break;
          case 'functions/permission-denied':
            errorMessage += 'Your email is not yet approved for this dashboard.';
            break;
          default:
            errorMessage += `Error: ${error.message || error.code || 'Unknown error'}`;
        }
      } else if (error?.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please check your configuration and try again.';
      }
      
      alert(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex flex-col items-center mb-6">
          <img
            src="/logo512.png"
            alt="Sunbeth Global Concepts"
            className="h-20 w-20 object-contain mb-4"
          />
          <h1 className="text-center text-3xl font-extrabold text-gray-900">
            Customer Satisfaction Monitoring System
          </h1>
          <p className="mt-2 text-center text-sm text-gray-500">
            Sunbeth Global Concepts
          </p>
        </div>
        <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
          Sign in to your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Internal users only. Sign in with Microsoft SSO.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div>
            <button
              onClick={handleMicrosoftSignIn}
              disabled={loading}
              className="w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Signing in...
                </>
              ) : (
                <>
                  <svg
                    className="w-5 h-5 mr-2"
                    viewBox="0 0 23 23"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.5 0C5.149 0 0 5.149 0 11.5S5.149 23 11.5 23 23 17.851 23 11.5 17.851 0 11.5 0z"
                      fill="#F25022"
                    />
                    <path
                      d="M11.5 0C5.149 0 0 5.149 0 11.5S5.149 23 11.5 23 23 17.851 23 11.5 17.851 0 11.5 0z"
                      fill="#7FBA00"
                    />
                    <path
                      d="M11.5 0C5.149 0 0 5.149 0 11.5S5.149 23 11.5 23 23 17.851 23 11.5 17.851 0 11.5 0z"
                      fill="#00A4EF"
                    />
                    <path
                      d="M11.5 0C5.149 0 0 5.149 0 11.5S5.149 23 11.5 23 23 17.851 23 11.5 17.851 0 11.5 0z"
                      fill="#FFB900"
                    />
                  </svg>
                  Sign in with Microsoft
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;

